import { API_CORE } from "../../../../API/ApiCORE";
import { ES, GB } from "country-flag-icons/react/3x2";
import { CONFIG_CONSTANTS } from "../../../../API/_Api.Config";
import { SPANISH } from "../../../../_Config/_Translate/Spanish";
import { ENGLISH } from "../../../../_Config/_Translate/English";
import { APP_STORAGE } from "../../../../_Config/AppConfig.Storage";
import { STORAGE_SESSION_HELPER } from "../../../../HELPERS/storageSession.helper";
import { onSetAlertTexts } from "../../../Templates/_UI/Confirm/_Templates/_swalHelper";
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);

    if (context === undefined) {
        return {
            useDataLanguage: {
                isLoading: false, currentLang: "es",
                translations: {}, availableLangs: ["es"],
                availableLangsMeta: [{ code: "es", label: "Español" }],
            },
            call_translate: (key) => key,
            call_toggleTranslate: async () => { },
            call_refreshTranslate: async () => ({})
        };
    }

    return context;
};

const DEFAULT_LANG = "es";
const LANG_META = {
    es: { code: "es", Flag: ES, label: "Español" },
    en: { code: "en", Flag: GB, label: "English" },
};
const SUPPORTED_LANGS = Object.keys(LANG_META);
const STATIC_TRANSLATIONS = { es: SPANISH, en: ENGLISH };

export const LanguageProvider = ({ children }) => {

    const [useDataLanguage, setDataLanguage] = useState({
        isLoading: true, currentLang: DEFAULT_LANG,
        translations: STATIC_TRANSLATIONS[DEFAULT_LANG],
        availableLangs: SUPPORTED_LANGS, availableLangsMeta: Object.values(LANG_META),
    });

    const resolveKey = (obj, path) => {
        return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
    };

    const CB__call_translate = useCallback((key, vars = {}) => {
        const value = resolveKey(useDataLanguage.translations, key);
        if (value === null || typeof value !== "string") return key;

        return value.replace(/\{\{(\w+)\}\}/g, (_, varName) => {
            return vars[varName] !== undefined ? vars[varName] : `{{${varName}}}`;
        });
    }, [useDataLanguage.translations]);

    const LOAD_GET_TRANSLATIONS = async (LANG_CODE) => {
        try {
            if (!SUPPORTED_LANGS.includes(LANG_CODE)) {
                throw new Error("Idioma no soportado");
            }

            const TRANSLATIONS = STATIC_TRANSLATIONS[LANG_CODE];

            if (!TRANSLATIONS) throw new Error("Traducciones no encontradas");

            onSetAlertTexts(TRANSLATIONS?.TEMPLATES?.alerts);

            setDataLanguage({
                isLoading: false, currentLang: LANG_CODE, translations: TRANSLATIONS,
                availableLangs: SUPPORTED_LANGS, availableLangsMeta: Object.values(LANG_META),
            });

            if (typeof document !== "undefined") {
                document.documentElement.lang = LANG_CODE;
            }

            return TRANSLATIONS;

        } catch (error) {
            setDataLanguage(prev => ({ ...prev, isLoading: false }));
            return null;
        }
    };

    useEffect(() => {
        let isMounted = true;

        const initLanguage = async () => {
            try {
                const SAVED_LANG = STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.APP_LANGUAGE);
                const BROWSER_LANG = typeof navigator !== "undefined" ? navigator.language.split("-")[0] : DEFAULT_LANG;

                const LANG_TO_LOAD = SAVED_LANG || (SUPPORTED_LANGS.includes(BROWSER_LANG) ? BROWSER_LANG : DEFAULT_LANG);

                if (!isMounted) return;
                await LOAD_GET_TRANSLATIONS(LANG_TO_LOAD);
            } catch (err) {
                if (isMounted) {
                    setDataLanguage(prev => ({ ...prev, isLoading: false }));
                }
            }
        };

        initLanguage();
        return () => { isMounted = false; };
    }, []);

    const CB__call_toggleTranslate = useCallback(async (LANG_CODE) => {
        if (!LANG_CODE || !SUPPORTED_LANGS.includes(LANG_CODE)) return;

        try {
            STORAGE_SESSION_HELPER.SET_STORAGE(APP_STORAGE.APP_LANGUAGE, LANG_CODE);
            await LOAD_GET_TRANSLATIONS(LANG_CODE);
        } catch (e) {
            // console.error("Error al cambiar idioma:", e);
        }
    }, []);

    const CB__call_refreshTranslate = useCallback(async () => {
        const LANG = STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.APP_LANGUAGE) || DEFAULT_LANG;
        return await LOAD_GET_TRANSLATIONS(LANG);
    }, []);

    return (
        <LanguageContext.Provider value={{
            useDataLanguage, call_translate: CB__call_translate, call_toggleTranslate: CB__call_toggleTranslate, call_refreshTranslate: CB__call_refreshTranslate
        }}>
            {children}
        </LanguageContext.Provider>
    );
};